import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router';

// API/Components
import axiosInstance from '../components/axios';
import Toast from '../components/toast';
import { isEmpty } from '../components/enums';
import SelectAllToolbar from '../components/select_all_toolbar';

// Material UI
import { DataGrid } from '@mui/x-data-grid';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Paper from '@mui/material/Paper';
import Switch from '@mui/material/Switch';
import Typography from '@mui/material/Typography';
import { styled } from '@mui/material/styles';

const StyledGridOverlay = styled('div')(({ theme }) => ({
	display: 'flex',
	flexDirection: 'column',
	alignItems: 'center',
	justifyContent: 'center',
	height: '100%',
	'& .ant-empty-img-1': {
		fill: theme.palette.mode === 'light' ? '#aeb8c2' : '#262626',
	},
	'& .ant-empty-img-2': {
		fill: theme.palette.mode === 'light' ? '#f5f5f7' : '#595959',
	},
	'& .ant-empty-img-3': {
		fill: theme.palette.mode === 'light' ? '#dce0e6' : '#434343',
	},
	'& .ant-empty-img-4': {
		fill: theme.palette.mode === 'light' ? '#fff' : '#1c1c1c',
	},
	'& .ant-empty-img-5': {
		fillOpacity: theme.palette.mode === 'light' ? '0.8' : '0.08',
		fill: theme.palette.mode === 'light' ? '#f5f5f5' : '#fff',
	},
}));

function NoUsersOverlay() {
	return (
		<StyledGridOverlay>
			<svg
				width="120"
				height="100"
				viewBox="0 0 184 152"
				aria-hidden
				focusable="false"
			>
				<g fill="none" fillRule="evenodd">
					<g transform="translate(24 31.67)">
						<ellipse
							className="ant-empty-img-5"
							cx="67.797"
							cy="106.89"
							rx="67.797"
							ry="12.668"
						/>
						<path
							className="ant-empty-img-1"
							d="M122.034 69.674L98.109 40.229c-1.148-1.386-2.826-2.225-4.593-2.225h-51.44c-1.766 0-3.444.839-4.592 2.225L13.56 69.674v15.383h108.475V69.674z"
						/>
						<path
							className="ant-empty-img-2"
							d="M33.83 0h67.933a4 4 0 0 1 4 4v93.344a4 4 0 0 1-4 4H33.83a4 4 0 0 1-4-4V4a4 4 0 0 1 4-4z"
						/>
						<path
							className="ant-empty-img-3"
							d="M42.678 9.953h50.237a2 2 0 0 1 2 2V36.91a2 2 0 0 1-2 2H42.678a2 2 0 0 1-2-2V11.953a2 2 0 0 1 2-2zM42.94 49.767h49.713a2.262 2.262 0 1 1 0 4.524H42.94a2.262 2.262 0 0 1 0-4.524zM42.94 61.53h49.713a2.262 2.262 0 1 1 0 4.525H42.94a2.262 2.262 0 0 1 0-4.525zM121.813 105.032c-.775 3.071-3.497 5.36-6.735 5.36H20.515c-3.238 0-5.96-2.29-6.734-5.36a7.309 7.309 0 0 1-.222-1.79V69.675h26.318c2.907 0 5.25 2.448 5.25 5.42v.04c0 2.971 2.37 5.37 5.277 5.37h34.785c2.907 0 5.277-2.421 5.277-5.393V75.1c0-2.972 2.343-5.426 5.25-5.426h26.318v33.569c0 .617-.077 1.216-.221 1.789z"
						/>
					</g>
					<path
						className="ant-empty-img-3"
						d="M149.121 33.292l-6.83 2.65a1 1 0 0 1-1.317-1.23l1.937-6.207c-2.589-2.944-4.109-6.534-4.109-10.408C138.802 8.102 148.92 0 161.402 0 173.881 0 184 8.102 184 18.097c0 9.995-10.118 18.097-22.599 18.097-4.528 0-8.744-1.066-12.28-2.902z"
					/>
					<g className="ant-empty-img-4" transform="translate(149.65 15.383)">
						<ellipse cx="20.654" cy="3.167" rx="2.849" ry="2.815" />
						<path d="M5.698 5.63H0L2.898.704zM9.259.704h4.985V5.63H9.259z" />
					</g>
				</g>
			</svg>
			<Box sx={{ mt: 1 }}>No Users</Box>
		</StyledGridOverlay>
	);
}

export default function LocationUsersManagement() {

	const { locationNum } = useParams();	
	const [locationInfo, setLocationInfo] = useState({});
	
	const [toast, setToast] = useState({
		open: false,
		severity: 'info',
		message: '',
	});

	// Collection of Users data. This should only change if requesting user's information changes. This instance would require a page refresh
	const [usersData, setUsersData] = useState([]);
	
	// List of users that have access to the location
	const [locUserPermissions, setLocUserPermissions] = useState([]);
	
	// Data show in the table
	const [tableData, setTableData] = useState([]);
	
	// Permissions management
	const [changesMade, setChangesMade] = useState(false);
	const [userIDsChanged, setUserIDsChanged] = useState([]);

	// The user information for the logged-in user
	const [userInfo, setUserInfo] = useState({});

	const [intLocationNum, setIntLocationNum] = useState(parseInt(locationNum, 10));

	const [selectAllState, setSelectAllState] = useState(true);

	// DataGrid pagination state
	const [page, setPage] = useState(0);
	const [pageSize, setPageSize] = useState(50);

	// DataGrid filter model state
	const [filterModel, setFilterModel] = useState({ items: [] });

	// User List table columns
	// TODO: FIX THE COMMENTED-OUT PERMISSIONS SO THAT ONCE ACCESS IS GRANTED, THEY CAN BE CHANGED WITHOUT PAGE REFRESH
	const [userColumns, setUserColumns] = useState([
		{
			field: 'first_name',
			headerName: 'First Name',
			headerAlign: "left",
			headerClassName: 'wrap-header-text' ,
			description: 'First Name',
			minWidth: 150,
			flex: 1,
		},
		{
			field: 'last_name',
			headerName: 'Last Name',
			headerAlign: "left",
			headerClassName: 'wrap-header-text' ,
			description: 'Last Name',
			minWidth: 150,
			flex: 1,
		},
		{
			field: 'email',
			headerName: 'Email',
			headerAlign: "left",
			headerClassName: 'wrap-header-text' ,
			description: 'Email Address',
			minWidth: 150,
			flex: 1,
		},
		{
			field: 'company',
			headerName: 'Company',
			headerAlign: 'center',
			headerClassName: 'wrap-header-text' ,
			description: 'Name of the Company that the user works for',
			minWidth: 100,
			flex: 1,
		},
		{
			field: 'is_staff',
			headerName: 'Is Admin',
			headerAlign: 'center',
			headerClassName: 'wrap-header-text' ,
			description: 'Is the user an admin? (Can manage users)',
			minWidth: 100,
			flex: 1,
			renderCell: (params) => renderYesNo(params.value),	
		},
		{
			field: 'is_active',
			headerName: 'Enabled',
			headerAlign: "center",
			description: 'Whether or not the user\'s account is enabled',
			minWidth: 100,
			flex: 1,
			renderCell: (params) => renderYesNo(params.value),
		},
		{
			field: 'has_access',
			headerName: 'Has Access',
			headerAlign: "center",
			description: 'Can the User view this location?',
			minWidth: 100,
			flex: 1,
			filterable: false,
			hideable: false,
			renderCell: (params) => {
				// If the user is a superuser, access should not be changeable
				return params.row.is_superuser ?
					renderYesNo(true) :
					renderSwitch(params.row.id, 'has_access', params.value)
			},
		},
		// {
		// 	field: 'can_change_settings',
		// 	headerName: 'System Settings',
		// 	headerAlign: "left",
		// 	description: 'Can the User change the location\'s system settings (not including limits)?',
		// 	minWidth: 100,
		// 	flex: 1,
		//	filterable: false,
		//	hideable: false,
		// 	renderCell: (params) => renderSwitchFromParams(params, 'can_change_settings'),
		// },
		// {
		// 	field: 'can_change_limits',
		// 	headerName: 'Change Limits',
		// 	headerAlign: "left",
		// 	description: 'Can the User change the location\'s limit settings?',
		// 	minWidth: 100,
		// 	flex: 1,
		//	filterable: false,
		//	hideable: false,
		// 	renderCell: (params) => renderSwitchFromParams(params, 'can_change_limits'),
		// },
		// {
		// 	field: 'can_change_email_settings',
		// 	headerName: 'Email Settings',
		// 	headerAlign: "left",
		// 	description: 'Can the User change the location\'s email settings (for all users)?',
		// 	minWidth: 100,
		// 	flex: 1,
		//	filterable: false,
		//	hideable: false,
		// 	renderCell: (params) => renderSwitchFromParams(params, 'can_change_email_settings'),
		// },
		// {
		// 	field: 'can_change_observer_settings',
		// 	headerName: 'Observer Settings',
		// 	headerAlign: "left",
		// 	description: 'Can the User change the location\'s observer settings?',
		// 	minWidth: 100,
		// 	flex: 1,
		//	filterable: false,
		//	hideable: false,
		// 	renderCell: (params) => renderSwitchFromParams(params, 'can_change_observer_settings'),
		// },
		// {
		// 	field: 'can_view_audit_log',
		// 	headerName: 'View Audit Log',
		// 	headerAlign: "left",
		// 	description: 'Can the User view the Audit Log?',
		// 	minWidth: 100,
		// 	flex: 1,
		//	filterable: false,
		//	hideable: false,
		// 	renderCell: (params) => renderSwitchFromParams(params, 'can_view_audit_log'),
		// },
		// {
		// 	field: 'can_view_notes',
		// 	headerName: 'View Notes',
		// 	headerAlign: "left",
		// 	description: 'Can the User view the Notes column across the site?',
		// 	minWidth: 100,
		// 	flex: 1,
		//	filterable: false,
		//	hideable: false,
		// 	renderCell: (params) => renderSwitchFromParams(params, 'can_view_notes'),
		// },
	]);

	// const renderSwitchFromParams = (params, switchName) => {
	// 	if (params.row.is_superuser) {
	// 		return renderDisabledSwitch(true);
	// 	} else {
	// 		return params.row.has_access ? 
	// 			renderSwitch(params.row.userId, switchName, params.value) :
	// 			renderDisabledSwitch(false);
	// 	}
	// }

	const navigate = useNavigate();

	// Get data about the user on the page
	useEffect(() => {
		axiosInstance.get(`/users/`).then(response => {		
			const userInfo = response.data;

			// Check if the user is a superuser - no editing of superusers access
			if (!userInfo || userInfo.length === 0 || (!userInfo.is_staff && !userInfo.is_superuser)) {
				navigate('/403/', { replace: true });
			}

			setUserInfo(response.data);
			
			// Enable an additional column if the user is a superuser
			if (response.data.is_superuser && userColumns.find(column => column.field === 'is_superuser') == undefined) {
				let newUserColumns = [...userColumns];
				
				newUserColumns.splice(4, 0, {
					field: 'is_superuser',
					headerName: 'Is Superuser',
					headerAlign: "center",
					description: 'Whether or not the user is a superuser',
					minWidth: 100,
					flex: 1,
					renderCell: (params) => renderYesNo(params.value),
				});
				setUserColumns(newUserColumns);
			}
		});
	}, []);

	// Get the updated user information that the logged-in user has access to
	useEffect(() => {
		setIntLocationNum(parseInt(locationNum, 10));

		axiosInstance.get(`/loc/${intLocationNum}/size/`)
			.then(response => {
				setLocationInfo(response.data);
			})
			.catch(error => {
				// If the user does not have access to the location, redirect to the 403 page
				if (error.response && error.response.status === 403) {
					navigate('/403/', { replace: true });
				}

				console.error(error);
				setLocationInfo({});
			});

		UpdateUsersData();
	}, [locationNum]);
	
	// Get the list of users that the admin/su has access to
	function UpdateUsersData() {
		axiosInstance.get(`/users/list/`)
			.then(response => {
				setUsersData(response.data);
				GetUsersAccessData();
			})
			.catch(error => {
				console.error(error);
				setUsersData([]);
			});
	}
	
	// Get the list of users that have access to the chosen location
	function GetUsersAccessData() {
		axiosInstance.get(`/users/locations/${intLocationNum}`)
			.then(response => {
				setLocUserPermissions(response.data);
			})
			.catch(error => {
				console.error(error);
				setLocUserPermissions([]);
			});
	}

	// Get the location information for the table
	useEffect(() => {
		BuildTableData(usersData, locUserPermissions);
	}, [locationNum, usersData, locUserPermissions]);
		
	// Merge the two lists of users to build the table
	function BuildTableData(accessUsersList, locationUsersList) {

		// Compare the two lists to see what the selected user has access to
		const combinedList = accessUsersList.map(user => {

			// Try to find a matching user location permission for each requesting user location permission
			let locationUser = undefined;
			if (locationUsersList && locationUsersList.length > 0) {
				locationUser = locationUsersList.find(locUser => locUser.user_id === user.id);
			}

			// If a matching user is found in locationUsersList, merge the two objects, using the user's permissions and ID
			//   otherwise, if no matching location is found in userLocationsList, create a new one and set all permissions fields to false
			if (locationUser) {
				return {
					...user,
					...locationUser,
					has_access: true,
				};
			} else {
				return {
					...user,
					has_access: false,
					change_system_settings: false,
					change_limits: false,
					change_email_settings: false,
					change_observer_settings: false,
					view_audit_log: false,
					view_notes: false
				};
			}
		});

		setTableData(combinedList);
	}

	// Get user's name and/or email for the page title
	function getPageTitle() {
		if (!isEmpty(locationInfo)) {
			if (locationInfo.location_name == '') {
				return "SN " + locationInfo.serial_number;
			} else {
				return locationInfo.location_name + " (SN " + locationInfo.serial_number + ")";
			}
		}

		return '';
	}

	// Utility to filter rows based on the filterModel
	function getFilteredRows(rows, filterModel) {
		if (!filterModel || !filterModel.items || filterModel.items.length === 0) return rows;

		return rows.filter(row => {
			return filterModel.items.every(filter => {
				if (!filter.value) return true;

				const value = row[filter.columnField];

				switch (filter.operatorValue) {
				case 'contains':
					return String(value).toLowerCase().includes(String(filter.value).toLowerCase());

				case 'equals':
					return String(value) === String(filter.value);

				case 'startsWith':
					return String(value).toLowerCase().startsWith(String(filter.value).toLowerCase());

				case 'endsWith':
					return String(value).toLowerCase().endsWith(String(filter.value).toLowerCase());

				case 'isEmpty':
					return value === null || value === undefined || value === '';

				case 'isNotEmpty':
					return value !== null && value !== undefined && value !== '';

				case 'isAnyOf':
					const filterValues = filter.value.split(',').map(v => v.trim().toLowerCase());
					return filterValues.includes(String(value).toLowerCase());

				default:
					return true;
				}
			});
		});
	}

	// Append the location ID to the list of locations that have been updated
	const handleIDsAppend = (newInteger) => {
		setUserIDsChanged(prevState => {
			if (!prevState.includes(newInteger)) {
				return [...prevState, newInteger];
			} else {
				return prevState;
			}
		});
	};

	// Handle the switch change event
	const handleSwitchChange = (userId, switchName, event) => {
		setChangesMade(true);
		handleIDsAppend(userId);

		setTableData(prevTableData => {
			return prevTableData.map(row => {
				if (row.id === userId) {
					return { ...row, [switchName]: event.target.checked };
				} else {
					return row;
				}
			});
		});
	};

	// Handle the Select All button click (works with filtering and pagination)
	const handleSelectAll = () => {
		setChangesMade(true);

		// Filter and paginate manually
		const filteredRows = getFilteredRows(tableData, filterModel);
		const startIndex = page * pageSize;
		const endIndex = startIndex + pageSize;
		const visibleRows = filteredRows.slice(startIndex, endIndex);

		const updatedUserIds = visibleRows
			.filter(row => !row.is_superuser)
			.map(row => row.id);

		setUserIDsChanged(updatedUserIds);

		setTableData(prevTableData =>
			prevTableData.map( row => updatedUserIds.includes(row.id) ? { ...row, has_access: selectAllState } : row )
		);

		setSelectAllState(!selectAllState);
	};
	
	const handleSaveClick = () => {
		if (userIDsChanged.length === 0) {
			setChangesMade(false);
			return;
		}

		// Build the list of changed users
		let changedUsers = tableData
			.map(userState => {
				if (userIDsChanged.includes(userState.id)) {
					return ({
						user_id: userState.id,
						has_access: userState.has_access,
						change_system_settings: userState.change_system_settings,
						change_limits: userState.change_limits,
						change_email_settings: userState.change_email_settings,
						change_observer_settings: userState.change_observer_settings,
						view_audit_log: userState.view_audit_log,
						view_notes: userState.view_notes,
					});
				}

				return undefined;
			})
			.filter(Boolean);  // Remove undefined entries
		
		// Post the changes to the backend
		axiosInstance.post(`/users/locations/${intLocationNum}/change/`, changedUsers)
			.then(response => {
				let failedChanges = [];

				if (response.data && response.data.length > 0) {
					response.data.results.forEach(changeResult => {
						// Check if any changes failed
						if (!changeResult.success) {
							const failedUser = usersData.find(user => user.id === changeResult.user_id);
							if (failedUser) {
								failedChanges.push({ email: failedUser.email, reason: changeResult.error });
							}
						}
					});
				}

				// Display a toast message based on the success of permission changes
				if (failedChanges.length > 0) {
					const failedText = failedChanges.map(change => `${change.email}: ${change.reason}`).join('\r\n')
					setToast({severity: 'error', message: `Failed to save changes for the following users: \r\n${failedText}`, open: true });
				} else {
					setToast({ severity: 'success', message: 'User permission changes saved successfully.', open: true });
				}
			})
			.catch(error => {
				console.error(error);
				setToast({ severity: 'error', message: 'An error occurred while saving changes. Please try again.', open: true });
			})
			.finally(() => {		
				setChangesMade(false);
				setUserIDsChanged([]);

				// Update the table data
				GetUsersAccessData();
			});
	};

	// Render a switch for a given user and switchName
	const renderSwitch = (userId, switchName, checked) => {
		return (
			<div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
				<Switch checked={checked} onChange={(event) => handleSwitchChange(userId, switchName, event)} color='primary' />
			</div>
		);
	}

	// Render a switch for a given user and switchName
	const renderYesNo = (checked) => {
		return (
			<div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
				<p><strong>{ checked ? 'Yes' : 'No' }</strong></p>
			</div>
		);
	}
	
	return (
		<Paper
			sx={{
				overflow: 'auto',
				boxShadow: '0',
				height: '100%',
				display: 'flex',
				flexDirection: 'column',
			}}
		>
			<Typography
				variant='h5'
				fontFamily='Saira, sans-serif'
				fontWeight='600'
				color='#0F1820'
				align='center'
				padding='5px'
			>
				Editing User Access for {getPageTitle()}
			</Typography>
			<DataGrid
				getRowId={(row) => row.id}
				rows={tableData}
				columns={userColumns}
				page={page}
				onPageChange={setPage}
				pageSize={pageSize}
				onPageSizeChange={setPageSize}
				rowsPerPageOptions={[5, 25, 50, 100]}
				filterModel={filterModel}
				onFilterModelChange={setFilterModel}
				density='compact'
				components={{
					Toolbar: () => <SelectAllToolbar
						onSelectAll={handleSelectAll}
						selectAllState={selectAllState}
						columnName='Has Access'
						itemType='users'
					/>,
					NoRowsOverlay: NoUsersOverlay,
				}}
				sx={{
					padding: 2,
				}}
			/>
			<Toast
				severity={toast.severity}
				message={toast.message}
				toastOpen={toast.open}
				setToastOpen={() => setToast({ ...toast, open: false })}
			/>
			{ changesMade && (
				<Paper style={{ padding: 10, margin: 1, border: '1px solid lightgray' }}>
					<Box display="flex" justifyContent="space-between" alignItems="right">
						<Typography variant="body1">You have unsaved changes. Would you like to save?</Typography>
						<Box>
							<Button variant="contained" color="primary" onClick={handleSaveClick}>
								Save
							</Button>
						</Box>
					</Box>
				</Paper>
			)}
		</Paper>
	);
}